import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, X, Loader2, Upload, Star } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useServices } from "@/lib/hooks";

const EMPTY = { before_image_url: "", after_image_url: "", video_url: "", extra_images: [], caption: "", completion_date: "", service_category: "", is_featured: false, order: 0 };

export default function GalleryManage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin-gallery"],
    queryFn: async () => (await api.get("/admin/gallery")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-gallery"] });
    qc.invalidateQueries({ queryKey: ["gallery"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    await api.delete(`/admin/gallery/${id}`);
    refresh();
    toast.success("Item deleted");
  };

  const toggleFeature = async (it) => {
    await api.put(`/admin/gallery/${it.id}`, { ...it, is_featured: !it.is_featured });
    refresh();
  };

  return (
    <div data-testid="gallery-manage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl">Gallery</h1>
          <p className="text-slate-500 mt-1">Upload before/after work and feature it on the homepage.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY, order: items.length })} data-testid="gallery-add-btn" className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-green-700">
          <Plus className="h-4.5 w-4.5" /> <span className="hidden sm:inline">Add item</span>
        </button>
      </div>

      {isLoading ? <p className="mt-6 text-slate-500">Loading…</p> : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {items.map((it) => (
            <div key={it.id} className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm" data-testid={`gallery-item-admin-${it.id}`}>
              <div className="relative aspect-video grid grid-cols-2 bg-slate-100">
                {it.video_url ? (
                  <video src={fileUrl(it.video_url)} className="col-span-2 h-full w-full object-cover" muted />
                ) : (
                  <>
                    {it.before_image_url ? <img src={fileUrl(it.before_image_url)} alt="" className="h-full w-full object-cover" /> : <div className="grid place-items-center text-xs text-slate-400">No before</div>}
                    {it.after_image_url ? <img src={fileUrl(it.after_image_url)} alt="" className="h-full w-full object-cover" /> : <div className="grid place-items-center text-xs text-slate-400">No after</div>}
                  </>
                )}
                {it.is_featured && <span className="absolute top-2 left-2 rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white flex items-center gap-1"><Star className="h-3 w-3 fill-white" /> Featured</span>}
              </div>
              <div className="p-4">
                <p className="font-semibold text-slate-900 text-sm line-clamp-1">{it.caption || "Untitled"}</p>
                <p className="text-xs text-slate-500 mt-0.5">{it.service_category} · {it.completion_date}{it.extra_images?.length ? ` · +${it.extra_images.length} photos` : ""}</p>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => toggleFeature(it)} data-testid={`gallery-feature-${it.id}`} className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold ${it.is_featured ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{it.is_featured ? "Featured" : "Feature"}</button>
                  <button onClick={() => setEditing(it)} data-testid={`gallery-edit-${it.id}`} className="h-9 w-9 rounded-lg bg-slate-100 grid place-items-center hover:bg-slate-200"><Pencil className="h-4 w-4 text-slate-600" /></button>
                  <button onClick={() => remove(it.id)} data-testid={`gallery-delete-${it.id}`} className="h-9 w-9 rounded-lg bg-red-50 grid place-items-center hover:bg-red-100"><Trash2 className="h-4 w-4 text-red-600" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <GalleryModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function GalleryModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const { data: services = [] } = useServices();
  const isNew = !item.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(field, data.url);
      toast.success("Uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const uploadExtras = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const current = form.extra_images || [];
    const remaining = 8 - current.length;
    if (remaining <= 0) { toast.error("Maximum 8 additional photos"); return; }
    setUploading("extras");
    try {
      const urls = [];
      for (const file of files.slice(0, remaining)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      set("extra_images", [...current, ...urls]);
      toast.success(`${urls.length} photo(s) added`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const removeExtra = (idx) => set("extra_images", (form.extra_images || []).filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    const payload = { before_image_url: form.before_image_url, after_image_url: form.after_image_url, video_url: form.video_url, extra_images: form.extra_images || [], caption: form.caption, completion_date: form.completion_date, service_category: form.service_category, is_featured: form.is_featured, order: Number(form.order) || 0 };
    try {
      if (isNew) await api.post("/admin/gallery", payload);
      else await api.put(`/admin/gallery/${form.id}`, payload);
      toast.success(isNew ? "Item added" : "Item updated");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100";
  const UploadBox = ({ field, label, accept }) => (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      <label className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-3 cursor-pointer hover:border-green-400">
        {uploading === field ? <Loader2 className="h-5 w-5 animate-spin text-green-600" /> : <Upload className="h-5 w-5 text-slate-400" />}
        <span className="text-sm text-slate-600">{form[field] ? "Uploaded ✓ — replace" : "Upload"}</span>
        <input type="file" accept={accept} className="hidden" onChange={(e) => upload(field, e)} data-testid={`gallery-upload-${field}`} />
      </label>
      {form[field] && (accept.includes("video") ? <video src={fileUrl(form[field])} className="mt-2 h-24 rounded-lg" muted /> : <img src={fileUrl(form[field])} alt="" className="mt-2 h-24 rounded-lg object-cover" />)}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="gallery-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-xl text-slate-900">{isNew ? "Add gallery item" : "Edit item"}</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-lg bg-slate-100 grid place-items-center"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <UploadBox field="before_image_url" label="Featured before (slider)" accept="image/*" />
            <UploadBox field="after_image_url" label="Featured after (slider)" accept="image/*" />
          </div>
          <UploadBox field="video_url" label="Video (optional)" accept="video/*" />
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-slate-700">Additional photos <span className="text-slate-400 font-normal">(thumbnail strip)</span></label>
              <span className="text-xs text-slate-400">{(form.extra_images?.length || 0)}/8</span>
            </div>
            {(form.extra_images?.length || 0) > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-2">
                {form.extra_images.map((url, i) => (
                  <div key={i} className="relative">
                    <img src={fileUrl(url)} alt="" className="h-16 w-full object-cover rounded-lg border border-slate-200" />
                    <button type="button" onClick={() => removeExtra(i)} data-testid={`gallery-extra-remove-${i}`} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white grid place-items-center shadow"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
            {(form.extra_images?.length || 0) < 8 && (
              <label className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-3 cursor-pointer hover:border-green-400">
                {uploading === "extras" ? <Loader2 className="h-5 w-5 animate-spin text-green-600" /> : <Upload className="h-5 w-5 text-slate-400" />}
                <span className="text-sm text-slate-600">Upload photos (select multiple)</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadExtras} data-testid="gallery-upload-extras" />
              </label>
            )}
          </div>
          <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Caption</label><input className={inputCls} value={form.caption} onChange={(e) => set("caption", e.target.value)} data-testid="gallery-form-caption" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Category</label>
              <select className={inputCls} value={form.service_category} onChange={(e) => set("service_category", e.target.value)} data-testid="gallery-form-category">
                <option value="">Select…</option>
                {services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Date</label><input type="date" className={inputCls} value={form.completion_date} onChange={(e) => set("completion_date", e.target.value)} data-testid="gallery-form-date" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4 items-center">
            <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Order</label><input type="number" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="gallery-form-order" /></div>
            <label className="flex items-center gap-3 mt-6"><input type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} className="h-4 w-4 accent-green-600" data-testid="gallery-form-featured" /><span className="text-sm font-semibold text-slate-700">Feature on homepage</span></label>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="mt-6 w-full rounded-xl bg-green-600 py-3 font-bold text-white hover:bg-green-700 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="gallery-form-save">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save item"}
        </button>
      </div>
    </div>
  );
}
